//#region imports
import i18next from 'i18next';
import { Stack } from '@mui/system';
import { useTour } from '@reactour/tour';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { IconButton, Tooltip } from '@mui/material';
import { library } from '@fortawesome/fontawesome-svg-core';
import { DataGridPro, GridToolbar } from '@mui/x-data-grid-pro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { DeleteOutlineOutlined as DeleteIcon } from '@mui/icons-material';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import {
  faAdd,
  fas,
  faCalendarPlus,
  faBan,
  faChalkboardUser,
  faHandshakeAngle,
  faStreetView,
  faScrewdriverWrench,
  faFlagCheckered,
  faSquarePlus,
  faPersonCirclePlus,
  faXmark,
  faThumbsUp,
  faSkullCrossbones,
  faPersonWalkingArrowRight,
  faCheckDouble,
} from '@fortawesome/free-solid-svg-icons';

import ModalE from '../Modal/Create/ModalE';
import customAxios from '../../utils/customAxios';
import { formatDateDDMMYYYY } from '../../utils/dateFormat';
import { GlobalVariables } from '../../globalVar';
import useEquipsTour from '../../tours/equipsTour';
import ModalEDelete from '../Modal/Delete/ModalEDelete';
import ModalEUpdate from '../Modal/Update/ModalEUpdate';
import { useTableLocale } from '../../hooks/useTableLocale';
import { useTranslationHook } from '../../hooks/UseTranslationHook';
import ModalEMultDelete from '../Modal/Delete/Multiple/ModalEMultDelete';

library.add(
  fas,
  faCalendarPlus,
  faBan,
  faChalkboardUser,
  faHandshakeAngle,
  faStreetView,
  faScrewdriverWrench,
  faFlagCheckered,
  faSquarePlus,
  faPersonCirclePlus,
  faXmark,
  faThumbsUp,
  faSkullCrossbones,
  faPersonWalkingArrowRight,
  faCheckDouble,
);

//#endregion

export const EquipsTableAxios = () => {
  // Config de hooks
  const [equipData, setEquipData] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [modalEUpdateShow, setModalEUpdateShow] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [modalEShow, setModalEShow] = useState(false);
  const [modalEDeleteShow, setModalEDeleteShow] = useState(false);
  const [modalEMultipleDeleteShow, setModalEMultipleDeleteShow] =
    useState(false);

  const { t } = useTranslation();

  const {
    equipsString,
    equipNameString,
    equipBrandString,
    equipStatusString,
    equipModelString,
    noEquipsString,
    multDeleteString,
    detailString,
    editString,
    logsString,
    deleteString,
    addString,
    viewTutorialString,
    createdAtString,
    createdByString,
    updatedAtString,
    updatedByString,
    notesString,
  } = useTranslationHook();

  const { setIsOpen, setSteps, setCurrentStep } = useTour();
  const navigate = useNavigate();

  const equipsTour = useEquipsTour(setModalEShow);

  useEffect(() => {
    // Check if equipsTour has not been viewed
    const hasSeenTour = localStorage.getItem('equipsTourViewed');

    if (!hasSeenTour) {
      setIsOpen(true);
      setSteps(equipsTour);
      setCurrentStep(0);
      // equipsTour viewed
      localStorage.setItem('equipsTourViewed', 'true');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setIsOpen, setSteps]);

  const restartTour = () => {
    setIsOpen(true);
    setSteps(equipsTour);
    setCurrentStep(0);
    localStorage.setItem('equipsTourViewed', 'false');
  };

  useEffect(() => {
    const updateEquipData = () => {
      setEquipData((prevData) => {
        return prevData.map((equip) => ({
          ...equip,
          is_active: t(`${equip.is_active}`),
        }));
      });
    };
    i18next.on('languageChanged', updateEquipData);

    return () => {
      i18next.off('languageChanged', updateEquipData);
    };
  }, [t]);

  const endpoint = `${GlobalVariables.getURL()}listEquipments`;

  const getData = async () => {
    setIsLoading(true);

    try {
      const response = await customAxios.get(endpoint, {
        headers: {
          Authorization: 'bearer ' + localStorage.getItem('token'),
        },
      });
      const equipData = response.data.map((equip) => {
        return {
          ...equip,
          is_active: t(`${equip.is_active}`),
        };
      });
      setEquipData(equipData);
      setIsLoading(false);
    } catch (error) {
      console.log(error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const manageEquipDelete = (equipInfo) => {
    var equipId = equipInfo.id;
    localStorage.setItem('equipIdToDelete', equipId);

    var equipName = equipInfo.equip_name;
    var equipBrand = equipInfo.brand_name;
    var equipUpdatedDate = equipInfo.updated_at;

    localStorage.setItem('equipNameToDelete', equipName);
    localStorage.setItem('equipBrandToDelete', equipBrand);
    localStorage.setItem('equipUpdatedAtToDelete', equipUpdatedDate);
  };

  const manageEquipMultipleDelete = () => {
    localStorage.setItem('equipMultipleDeleteList', rowSelectionModel);
  };

  const manageEquipLogs = (equipInfo) => {
    localStorage.setItem('equipIdForLog', equipInfo.id);
    navigate('/equipLogs');
  };

  const manageEquipUpdate = (equipInfo) => {
    return new Promise((resolve, reject) => {
      var equipId = equipInfo.id;
      localStorage.setItem('equipIdForUpdate', equipId);

      customAxios
        .get(`${GlobalVariables.getURL()}autofillEquipo/${equipId}`, {
          headers: {
            Authorization: 'bearer ' + localStorage.getItem('token'),
          },
        })
        .then((response) => {
          var equipData = response.data;
          var equipNameForUPlaceholder = equipData.equipInfo.equip_name;
          localStorage.setItem(
            'equipNameForUPlaceholder',
            equipNameForUPlaceholder,
          );
          localStorage.setItem(
            'equipBrandForUPlaceholder',
            equipData.equipInfo.brand_name,
          );
          localStorage.setItem(
            'equipModelForUPlaceholder',
            equipData.equipInfo.model,
          );
          localStorage.setItem(
            'equipModelIdForUPlaceholder',
            equipData.equipInfo.model_id,
          );
          var equipDescriptionForUPlaceholder = equipData.equipInfo.description;
          localStorage.setItem(
            'equipDescriptionForUPlaceholder',
            equipDescriptionForUPlaceholder,
          );
          localStorage.setItem(
            'equipBusinessUnitForUPlaceholder',
            equipData.equipInfo.business_unit,
          );
          var equipNotesForU = equipData.equipInfo.notes;
          localStorage.setItem('equipNotesForU', equipNotesForU);
          resolve();
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  };

  const actionColumn = [
    {
      field: 'action',
      headerName: detailString,
      headerClassName: 'equips-datagrid-actions-tour',
      width: 210,
      renderCell: (params) => {
        return (
          <div className="cellAction equips-datagrid-actions-tour">
            <Tooltip title={editString} arrow>
              <button
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  marginTop: '5px',
                }}
                onClick={async () => {
                  try {
                    await manageEquipUpdate(params.row);
                    setModalEUpdateShow(true);
                  } catch (error) {
                    console.log(error);
                  }
                }}
              >
                <ModeEditOutlineOutlinedIcon
                  className="detail-icons"
                  id="update-icon"
                />
              </button>
            </Tooltip>
            <Tooltip
              title={
                <div>
                  {logsString} <br />{' '}
                  <span style={{ color: 'rgb(238, 105, 105)' }}>
                    *COMING SOON*
                  </span>
                </div>
              }
              arrow
            >
              <button
                disabled
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  marginTop: '5px',
                  display: 'none',
                }}
                onClick={() => {
                  manageEquipLogs(params.row);
                }}
              >
                <HistoryOutlinedIcon
                  className="detail-icons"
                  id="update-icon"
                />
              </button>
            </Tooltip>
            <Tooltip title={deleteString} arrow>
              <button
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  marginTop: '5px',
                }}
                onClick={() => {
                  setModalEDeleteShow(true);
                  manageEquipDelete(params.row);
                }}
              >
                <DeleteIcon className="detail-icons" id="delete-icon" />
              </button>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  // Columnas
  const equipColumns = [
    {
      field: 'id',
      headerName: 'ID',
      width: 100,
    },
    {
      field: 'equip_name',
      headerName: equipNameString,
      width: 180,
    },
    {
      field: 'brand_name',
      headerName: equipBrandString,
      width: 180,
    },
    {
      field: 'model',
      headerName: equipModelString,
      width: 180,
    },
    {
      field: 'business_unit',
      headerName: 'Unidad de Negocio',
      width: 180,
    },
    {
      field: 'is_active',
      headerName: equipStatusString,
      width: 150,
      renderCell: (params) => {
        if (
          params.row.is_active === 'Activo' ||
          params.row.is_active === 'Active'
        ) {
          return (
            <div>
              <span className="statusActive">{params.row.is_active}</span>
            </div>
          );
        } else {
          return (
            <div>
              <span className="statusInactive">{params.row.is_active}</span>
            </div>
          );
        }
      },
      valueGetter: (params) => params.row.is_active,
    },
    {
      field: 'created_at',
      headerName: createdAtString,
      width: 150,
    },
    {
      field: 'created_by',
      headerName: createdByString,
      width: 150,
    },
    {
      field: 'updated_at',
      headerName: updatedAtString,
      width: 150,
    },
    {
      field: 'updated_by',
      headerName: updatedByString,
      width: 150,
    },
    {
      field: 'notes',
      headerName: notesString,
      width: 200,
    },
    // Map through all columns and add default renderCell if not already defined
  ].map((col) =>
    col.renderCell
      ? col
      : {
          ...col,
          renderCell: (params) =>
            ['created_at', 'updated_at'].includes(col.field)
              ? formatDateDDMMYYYY(params.row[col.field])
              : params.row[col.field] || '-',
        },
  );

  const locale = useTableLocale();

  return (
    <>
      <div className="datatable-btns-container">
        <div className="dt-btn-main-container">
          <span className="dabiWebText">{equipsString} </span>
          <button
            className="add-button create-equip-tour"
            onClick={() => setModalEShow(true)}
          >
            {addString} <FontAwesomeIcon icon={faAdd} />
          </button>

          <button
            disabled={isButtonDisabled}
            onClick={() => {
              setModalEMultipleDeleteShow(true);
              manageEquipMultipleDelete();
            }}
            className="multiple-delete-btn"
          >
            {multDeleteString}
          </button>
        </div>
        <Tooltip title={viewTutorialString}>
          <IconButton
            className="tour-button"
            size="medium"
            onClick={() => restartTour()}
          >
            <FontAwesomeIcon icon={faCircleInfo} />
          </IconButton>
        </Tooltip>
      </div>

      <ModalE
        show={modalEShow}
        onHide={() => setModalEShow(false)}
        getEquips={getData}
        className="create-equip-form-tour"
      />

      <ModalEMultDelete
        show={modalEMultipleDeleteShow}
        onHide={() => setModalEMultipleDeleteShow(false)}
        getEquips={getData}
      />

      <ModalEDelete
        show={modalEDeleteShow}
        onHide={() => setModalEDeleteShow(false)}
        getEquips={getData}
      />

      <ModalEUpdate
        show={modalEUpdateShow}
        onHide={() => setModalEUpdateShow(false)}
        getEquips={getData}
      />

      <DataGridPro
        localeText={locale.components.MuiDataGrid.defaultProps.localeText}
        pagination
        loading={isLoading}
        initialState={{
          sorting: {
            sortModel: [{ field: 'id', sort: 'desc' }],
          },
          columns: {
            columnVisibilityModel: {
              id: false,
            },
          },
          pinnedColumns: { right: ['action'] },
        }}
        rows={equipData}
        columns={equipColumns.concat(actionColumn)}
        pageSize={5}
        rowsPerPageOptions={[5]}
        checkboxSelection
        onRowSelectionModelChange={(newRowSelectionModel) => {
          setRowSelectionModel(newRowSelectionModel);
          if (newRowSelectionModel.length >= 1) {
            setIsButtonDisabled(false);
          }
          if (newRowSelectionModel.length === 0) {
            setIsButtonDisabled(true);
          }
        }}
        rowSelectionModel={rowSelectionModel}
        slots={{
          noRowsOverlay: () => (
            <Stack height="100%" alignItems="center" justifyContent="center">
              {noEquipsString}
            </Stack>
          ),
          toolbar: GridToolbar,
        }}
        className="equips-datagrid-tour"
      />
    </>
  );
};
